import { useState, useEffect, useCallback } from 'react';
import { Drawer, Tabs, Button, Typography, Grid } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import { DrawerViewTitle } from '@/components/DrawerViewTitle';
import { CameraTab } from '@/components/CameraTab';
import { TimerTab } from '@/components/TimerTab';
import { MODAL_BLUR_STYLES } from '@/styles/modalStyles';
import type { View, CameraSettings, TimerSettings } from '@/types';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';

const { Text } = Typography;

function isTestShotDisabledDslr(view: View): boolean {
  const isDslrMode = view.settings?.camera?.mode === 'dslr';
  const isDslrConnected = view.androidStatus?.device?.dslr?.connected ?? false;
  return isDslrMode && !isDslrConnected;
}

interface SettingsDrawerProps {
  open: boolean;
  view: View | null;
  onClose: () => void;
  onTestShot: (view: View) => void;
  onSaveCameraSettings: (settings: CameraSettings) => Promise<void>;
  onSaveTimerSettings: (capturing: boolean, settings?: TimerSettings) => Promise<void>;
  activeTab?: string;
  highlightFieldId?: string | null;
  onHighlightComplete?: () => void;
}

export function SettingsDrawer({
  open,
  view,
  onClose,
  onTestShot,
  onSaveCameraSettings,
  onSaveTimerSettings,
  activeTab: activeTabProp,
  highlightFieldId,
  onHighlightComplete,
}: SettingsDrawerProps) {
  const [activeTab, setActiveTab] = useState<string>(activeTabProp ?? '1');
  const [timerSaveHandler, setTimerSaveHandler] = useState<(() => Promise<void>) | null>(null);
  const [timerHasChanges, setTimerHasChanges] = useState(false);
  const [timerCancelHandler, setTimerCancelHandler] = useState<(() => void) | null>(null);
  const [timerIsSaving, setTimerIsSaving] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm; // <576px: full-screen drawer
  const connectionStatus = useConnectionStatus(view?.token, view?.androidStatus);

  useEffect(() => {
    if (activeTabProp) {
      setActiveTab(activeTabProp);
    }
  }, [activeTabProp]);

  const handleClose = () => {
    setActiveTab('1');
    setTimerSaveHandler(null);
    setTimerHasChanges(false);
    setTimerCancelHandler(null);
    onClose();
  };

  const handleTimerSave = useCallback(async (capturing: boolean, settings?: TimerSettings) => {
    // The capture toggle comes through here too, with no settings: it writes
    // `capturing` alone and has its own spinner on the switch. Only a real form
    // save may drive the Save button, otherwise flipping the toggle spins a button
    // that is saving nothing.
    const isFormSave = settings !== undefined;
    if (isFormSave) setTimerIsSaving(true);
    try {
      await onSaveTimerSettings(capturing, settings);
    } finally {
      if (isFormSave) setTimerIsSaving(false);
    }
  }, [onSaveTimerSettings]);

  const handleTimerSaveHandlerReady = useCallback((handler: () => Promise<void>, hasChanges: boolean, cancelHandler: () => void) => {
    setTimerSaveHandler(() => handler);
    setTimerHasChanges(hasChanges);
    setTimerCancelHandler(() => cancelHandler);
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const contentHolder = document.querySelector('.ant-drawer-body .ant-tabs-content-holder');
    if (contentHolder) {
      contentHolder.scrollTop = 0;
    }
    if (key !== '2') {
      setTimerSaveHandler(null);
      setTimerHasChanges(false);
      setTimerCancelHandler(null);
    }
    onHighlightComplete?.();
  };

  return (
    <Drawer
      rootClassName="drawer-dark-header"
      title={<DrawerViewTitle view={view} />}
      placement="right"
      width={isMobile ? '100%' : 360}
      open={open}
      closeIcon={null}
      extra={
        <Button
          type="default"
          icon={<MaterialIcon name="close" size={20} />}
          onClick={handleClose}
        />
      }
      onClose={handleClose}
      styles={MODAL_BLUR_STYLES}
      footer={
        activeTab === '1' && view ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button
              type="primary"
              onClick={() => {
                onTestShot(view);
                handleClose();
              }}
              size="large"
              icon={<MaterialIcon name="photo_camera" size={18} />}
              disabled={
                connectionStatus !== 'online' ||
                isTestShotDisabledDslr(view)
              }
            >
              Test Shot
            </Button>
          </div>
        ) : activeTab === '2' && timerSaveHandler ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', width: '100%' }}>
            <Button
              onClick={timerCancelHandler || undefined}
              size="large"
              disabled={!timerHasChanges || timerIsSaving}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={timerSaveHandler}
              size="large"
              loading={timerIsSaving}
              disabled={!timerHasChanges}
            >
              Save timer settings
            </Button>
          </div>
        ) : null
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: '1',
            label: 'Camera',
            icon: <MaterialIcon name="photo_camera" size={22} />,
            children: view ? (
              <CameraTab
                view={view}
                viewId={view.viewId}
                onSave={onSaveCameraSettings}
                hideTestShotButton={true}
              />
            ) : (
              <Text>No view selected</Text>
            ),
          },
          {
            key: '2',
            label: 'Timer',
            icon: <MaterialIcon name="avg_pace" size={22} />,
            children: view ? (
              <TimerTab
                view={view}
                onSave={handleTimerSave}
                onSaveHandlerReady={handleTimerSaveHandlerReady}
                hideSaveButton={true}
                highlightFieldId={activeTab === '2' ? highlightFieldId : null}
                onHighlightComplete={onHighlightComplete}
              />
            ) : (
              <Text>No view selected</Text>
            ),
          },
        ]}
      />
    </Drawer>
  );
}
